import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Instagram } from "lucide-react";
import { CASE_STUDIES } from "@/content/site";
import { InstagramEmbed } from "@/components/media/InstagramEmbed";
import { useMedia } from "@/context/MediaContext";
import { MediaFrame } from "@/components/media/MediaFrame";
import { Lightbox } from "@/components/media/Lightbox";
import { Eyebrow, Reveal } from "@/components/site/Reveal";

const FIELDS = [["The situation", "situation"], ["What we noticed", "noticed"], ["The idea", "idea"], ["What we created", "created"], ["What happened", "happened"]];

export const CaseStory = ({ story, media, flip, onOpen }) => {
  const hasMedia = media && !media.placeholder;
  const showMedia = story.reel || hasMedia;
  return (
  <article className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-start" data-testid={`case-story-${story.id}`}>
    {showMedia && (
      <div className={`lg:col-span-5 ${flip ? "lg:col-start-8" : ""}`}>
        {story.reel ? (
          <InstagramEmbed url={story.reel} className="w-full" />
        ) : (
          <MediaFrame item={media} onOpen={onOpen} ratio="aspect-[4/5]" autoplay hoverPlay={false} className="rounded-sm" sizes="(min-width:1024px) 40vw, 100vw" />
        )}
      </div>
    )}
    <div className={showMedia ? `lg:col-span-6 ${flip ? "lg:col-start-1 lg:row-start-1" : "lg:col-start-7"}` : "lg:col-span-9"}>
      <p className="label-eyebrow text-[#38BDF8]">{story.industry} · {story.id}</p>
      <h3 className="mt-4 font-display uppercase text-3xl md:text-4xl tracking-tight text-[#2E1B16]">{story.title}</h3>
      {story.placeholder && (
        <p className="mt-3 font-annotation text-2xl text-[#2E1B16]/55" data-testid="case-placeholder-note">placeholder until the real story is written</p>
      )}
      <dl className="mt-8 divide-y divide-[#2E1B16]/10">
        {FIELDS.map(([label, key]) => (
          <div key={key} className="grid sm:grid-cols-[10rem_1fr] gap-2 sm:gap-6 py-4">
            <dt className="label-eyebrow text-[#2E1B16]/45 pt-1">{label}</dt>
            <dd className={`text-lg leading-relaxed ${story.placeholder ? "text-[#2E1B16]/40 italic" : "text-[#2E1B16]/80"}`}>{story[key]}</dd>
          </div>
        ))}
      </dl>
      {story.instagram && (
        <a href={story.instagram} target="_blank" rel="noopener noreferrer" data-testid={`case-instagram-${story.id}`}
          className="group mt-8 inline-flex items-center gap-3 rounded-full border border-[#2E1B16]/20 px-6 py-3 font-semibold text-[#2E1B16] hover:border-[#38BDF8] hover:text-[#38BDF8] transition-colors">
          <Instagram size={18} />
          Visit {story.instagramHandle || "their Instagram"}
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      )}
      {story.caseStudyPath && (
        <div className="mt-6">
          <Link to={story.caseStudyPath} data-testid={`case-read-${story.id}`}
            className="group inline-flex items-center gap-2 font-display text-xl md:text-2xl text-[#2E1B16] hover:text-[#38BDF8] transition-colors">
            Read the full story <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </div>
      )}
    </div>
  </article>
  );
};

const WorkStories = ({ limit = 2, showMore = true }) => {
  const { items } = useMedia("case", 3);
  const [open, setOpen] = useState(null);
  return (
    <section id="stories" className="py-32 md:py-48" data-testid="work-stories" aria-labelledby="stories-heading">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <Reveal><Eyebrow>Proof</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 id="stories-heading" className="mt-7 font-display uppercase text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[0.95] text-[#2E1B16]">
              The work behind<br />the work.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-lg md:text-xl text-[#2E1B16]/60 leading-relaxed max-w-md">
              No made up numbers. Just what the business looked like, what we spotted, what we made and what happened after.
            </p>
          </Reveal>
        </div>

        <div className="mt-20 md:mt-28 space-y-28 md:space-y-40">
          {CASE_STUDIES.slice(0, limit).map((s, i) => (
            <Reveal key={s.id} y={40}><CaseStory story={s} media={items[i]} flip={i % 2 === 1} onOpen={setOpen} /></Reveal>
          ))}
        </div>

        {showMore && (
          <Reveal delay={0.1}>
            <div className="mt-20 flex justify-end">
              <Link to="/work" data-testid="stories-more" className="group inline-flex items-center gap-3 font-display text-xl md:text-2xl text-[#2E1B16] hover:text-[#38BDF8] transition-colors">
                All the stories <ArrowRight size={22} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </div>
          </Reveal>
        )}
      </div>
      <Lightbox item={open} onClose={() => setOpen(null)} />
    </section>
  );
};

export default WorkStories;
